import type { SsoProviderKind } from '@zeus/index'
import { useMutation } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { GQL_DELETE_SSO_PROVIDER } from '~/gql/sso/sso'

type IProps = {
  open: boolean
  onCancel: () => void
  onSuccess: () => void
  kind: SsoProviderKind
  label: string
}

/**
 * Confirmation avant de retirer un fournisseur d'identité (#40).
 *
 * L'action est irréversible et emporte le client secret, qui ne ressort jamais
 * de l'API : le rétablir suppose d'en réémettre un côté IdP. Si le fournisseur
 * était activé, elle coupe aussi la connexion des apprenants qui passent par
 * lui — d'où l'avertissement, à l'image des suppressions d'utilisateur et
 * d'organisation.
 */
function SsoProviderDeleteModal({ open, onCancel, onSuccess, kind, label }: IProps) {
  const { t } = useTranslation()

  const [deleteProvider] = useMutation(GQL_DELETE_SSO_PROVIDER)

  const handleDelete = async () => {
    try {
      await deleteProvider({ variables: { kind } })
      toast.success(t('sso.deleted'))
      onSuccess()
      onCancel()
    } catch {
      toast.error(t('sso.deleteError'))
      onCancel()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sso.delete.title', { provider: label })}</DialogTitle>
          <DialogDescription>
            {t('sso.delete.first-message')}
            <br />
            <span className="font-bold">{t('delete.second-message')}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SsoProviderDeleteModal
